import { useCallback, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, ScanSearch, Upload } from "lucide-react";
import { DocumentParseDialog } from "@/components/DocumentParseDialog";
import {
  downloadGeometryCoordsTemplate,
  GEOMETRY_COORDS_GEOJSON_TEMPLATE,
  GEOMETRY_COORDS_TXT_TEMPLATE_LATLON,
  GEOMETRY_COORDS_TXT_TEMPLATE_LONLAT,
  parseGeometryCoordsFromText,
  type CoordinateOrder,
  type GeometryCoordsParseResult,
} from "@/lib/geometry-coords-import";

export type GeometryCoordsImportSuccess = Extract<GeometryCoordsParseResult, { ok: true }>;

interface GeometryCoordsImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (result: GeometryCoordsImportSuccess) => void;
}

export function GeometryCoordsImportDialog({
  open,
  onOpenChange,
  onImport,
}: GeometryCoordsImportDialogProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [order, setOrder] = useState<CoordinateOrder>("lonlat");
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"text" | "file">("text");
  const [documentParseOpen, setDocumentParseOpen] = useState(false);
  const [fromDocumentHint, setFromDocumentHint] = useState<string | null>(null);

  const reset = useCallback(() => {
    setText("");
    setOrder("lonlat");
    setError(null);
    setTab("text");
    setDocumentParseOpen(false);
    setFromDocumentHint(null);
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) reset();
      onOpenChange(next);
    },
    [onOpenChange, reset],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setText(String(reader.result ?? ""));
      setError(null);
    };
    reader.onerror = () => setError("Не удалось прочитать файл.");
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleSubmit = () => {
    const result = parseGeometryCoordsFromText(text, { coordinateOrder: order });
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(null);
    onImport(result);
    handleOpenChange(false);
  };

  const downloadGeoJsonTemplate = () => {
    downloadGeometryCoordsTemplate(
      "geopulseml-geometry-template.geojson",
      GEOMETRY_COORDS_GEOJSON_TEMPLATE,
      "application/geo+json;charset=utf-8",
    );
  };

  const downloadCoordsTxtTemplate = () => {
    const content =
      order === "latlon" ? GEOMETRY_COORDS_TXT_TEMPLATE_LATLON : GEOMETRY_COORDS_TXT_TEMPLATE_LONLAT;
    const suffix = order === "latlon" ? "latlon" : "lonlat";
    downloadGeometryCoordsTemplate(`geopulseml-coords-template-${suffix}.txt`, content);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="z-[220] max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Импорт координат</DialogTitle>
          <DialogDescription>
            Вставьте GeoJSON или список координат (WGS84). Можно скачать шаблон, подставить свои точки и
            загрузить обратно. После разбора — preview и сохранение как после рисования. Координаты из
            документа или фото — через «Распознать из документа».
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={downloadGeoJsonTemplate}>
            <Download className="mr-1.5 h-4 w-4" />
            Скачать шаблон GeoJSON
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={downloadCoordsTxtTemplate}>
            <Download className="mr-1.5 h-4 w-4" />
            Скачать шаблон координат
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDocumentParseOpen(true)}
          >
            <ScanSearch className="mr-1.5 h-4 w-4" />
            Распознать из документа
          </Button>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "text" | "file")} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="text">Текст</TabsTrigger>
            <TabsTrigger value="file">Файл</TabsTrigger>
          </TabsList>
          <TabsContent value="text" className="mt-3 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="geometry-coords-import-text">GeoJSON или список координат</Label>
              <Textarea
                id="geometry-coords-import-text"
                rows={10}
                placeholder={`Пример GeoJSON:\n{"type":"Polygon","coordinates":[[[37.6,55.7],[37.7,55.7],[37.7,55.8],[37.6,55.8],[37.6,55.7]]]}\n\nПример координат (долгота широта):\n37.61 55.75\n37.62 55.76\n37.63 55.75`}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setError(null);
                }}
                className="font-mono text-xs"
              />
            </div>
          </TabsContent>
          <TabsContent value="file" className="mt-3 space-y-3">
            <p className="text-sm text-muted-foreground">
              Поддерживаются <code className="rounded bg-muted px-1">.geojson</code>,{" "}
              <code className="rounded bg-muted px-1">.json</code>,{" "}
              <code className="rounded bg-muted px-1">.txt</code>,{" "}
              <code className="rounded bg-muted px-1">.csv</code> — содержимое читается в браузере. Скачайте
              шаблон выше, отредактируйте и выберите файл.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".geojson,.json,.txt,.csv,application/geo+json,text/plain,text/csv"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
              <Upload className="mr-2 h-4 w-4" />
              Выбрать файл
            </Button>
            {text.trim() ? (
              <p className="text-xs text-muted-foreground">
                Загружено символов: {text.length.toLocaleString("ru-RU")}
              </p>
            ) : null}
            {text.trim() ? (
              <Textarea
                rows={6}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setError(null);
                }}
                className="font-mono text-xs"
                aria-label="Содержимое файла"
              />
            ) : null}
          </TabsContent>
        </Tabs>

        <div className="space-y-2">
          <Label>Порядок координат (для текстового списка, не для GeoJSON)</Label>
          <Select
            value={order}
            onValueChange={(v) => setOrder(v as CoordinateOrder)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[250]">
              <SelectItem value="lonlat">Долгота, широта (как в GeoJSON)</SelectItem>
              <SelectItem value="latlon">Широта, долгота</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {fromDocumentHint ? (
          <p className="text-xs text-muted-foreground">{fromDocumentHint}</p>
        ) : null}

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Отмена
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!text.trim()}>
            Импортировать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <DocumentParseDialog
      open={documentParseOpen}
      onOpenChange={setDocumentParseOpen}
      title="Распознать координаты из документа"
      coordinateOrder={order}
      onReadyText={(readyText) => {
        setText(readyText);
        setTab("text");
        setError(null);
        setFromDocumentHint(
          "Текст подставлен из распознавания. Проверьте и нажмите «Импортировать».",
        );
      }}
    />
    </>
  );
}
